import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import API_ENDPOINTS from '../api/apiConfig';
import { env } from '../config/env';
import '../assets/css/theme.css';
import '../assets/css/shop.css';   // shared page-header band (.ffs-hero*)
import '../assets/css/pages.css';

function decodeHtml(html: string): string {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

interface TocItem {
  id: string;
  text: string;
  level: 2 | 3;
}

/** Links shown in the sidebar (same pages the footer links to). */
const PAGE_LINKS = [
  { slug: 'about-us', label: 'About Us' },
  { slug: 'privacy-policy', label: 'Privacy Policy' },
  { slug: 'return-policy', label: 'Returns' },
  { slug: 'shipping-policy', label: 'Shipping Policy' },
  { slug: 'terms-conditions', label: 'Terms & Conditions' },
];

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** Title with the last word set in gold italic (matches the other pages). */
const renderTitle = (raw: string) => {
  const words = raw.trim().split(/\s+/);
  if (words.length === 1) return <em>{raw}</em>;
  const last = words.pop();
  return (
    <>
      {words.join(' ')} <em>{last}</em>
    </>
  );
};

interface PagesProps {
  token?: string | null;
}

const Pages = ({ token }: PagesProps) => {
  const { slug } = useParams();

  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [visible, setVisible] = useState(false);

  const API_URL = `${API_ENDPOINTS.GET_PAGES}?slug=${slug}`;

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      setVisible(false);
      setError('');

      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(API_URL, { method: 'GET', headers });
        const data = await response.json();

        if (response.ok && data.status) {
          setTitle(data.page.page_title);
          setDescription(decodeHtml(data.page.page_description));
        } else {
          setError(data.message || 'Page not found.');
          setDescription('<p>Page not found.</p>');
        }
      } catch (err) {
        console.error('Error fetching page:', err);
        setError('An error occurred while fetching the page.');
        setDescription('<p>Error loading page.</p>');
      }

      setLoading(false);
      setTimeout(() => setVisible(true), 50);
    };

    fetchPage();
  }, [slug, token]);

  const displayTitle = title || (slug ? slug.replace(/-/g, ' ') : '');

  // Add ids to headings + build a table of contents from the CMS HTML
  const { html, toc } = useMemo(() => {
    if (!description) return { html: '', toc: [] as TocItem[] };
    const doc = new DOMParser().parseFromString(description, 'text/html');
    const items: TocItem[] = [];
    const used = new Set<string>();

    doc.body.querySelectorAll('h1, h2, h3').forEach((h, i) => {
      const text = (h.textContent || '').trim();
      if (!text) return;
      let id = slugify(text) || `section-${i}`;
      while (used.has(id)) id = `${id}-${i}`;
      used.add(id);
      h.id = id;
      items.push({ id, text, level: h.tagName === 'H3' ? 3 : 2 });
    });

    return { html: doc.body.innerHTML, toc: items };
  }, [description]);

  // highlight the section currently being read
  const [activeId, setActiveId] = useState<string>('');
  useEffect(() => {
    if (toc.length < 2) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-110px 0px -70% 0px' }
    );
    toc.forEach((t) => {
      const el = document.getElementById(t.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [toc, visible]);

  return (
    <main className="ffp-page">
      {/* ── Header band ── */}
      <header className="ffs-hero">
        <div className="ffh-container ffs-hero-inner">
          <div>
            <nav className="ffs-crumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span className="is-current">{displayTitle || 'Page'}</span>
            </nav>
            <h1 className="ffs-hero-title">{loading ? <em>&nbsp;</em> : renderTitle(displayTitle)}</h1>
          </div>
          <p className="ffs-hero-sub">Fashion Frame — curated with intention, worn with purpose.</p>
        </div>
      </header>

      <div className="ffh-container">
        <div className="ffp-layout">

          {/* ── Sidebar ── */}
          <aside className="ffp-side">
            {toc.length > 1 && (
              <nav className="ffp-card ffp-toc" aria-label="On this page">
                <p className="ffp-side-title">On this page</p>
                <ul>
                  {toc.map((t) => (
                    <li key={t.id} className={t.level === 3 ? 'is-sub' : ''}>
                      <a
                        href={`#${t.id}`}
                        className={activeId === t.id ? 'is-active' : ''}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          setActiveId(t.id);
                        }}
                      >
                        {t.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            <nav className="ffp-card" aria-label="More pages">
              <p className="ffp-side-title">More from Fashion Frame</p>
              <ul className="ffp-links">
                {PAGE_LINKS.map((p) => (
                  <li key={p.slug}>
                    <Link to={`/pages/${p.slug}`} className={slug === p.slug ? 'is-active' : ''}>
                      {p.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/contact">Contact Us</Link>
                </li>
              </ul>
            </nav>

            <div className="ffp-card ffp-help">
              <p className="ffp-side-title">Need help?</p>
              <a href={`mailto:${env.supportEmail}`}>{env.supportEmail}</a>
              <a href={`tel:${env.supportPhone}`}>{env.supportPhoneDisplay}</a>
            </div>
          </aside>

          {/* ── Content ── */}
          <article className={`ffp-content${visible ? ' is-visible' : ''}`}>
            {loading ? (
              <div className="ffp-skel" aria-busy="true">
                <span style={{ width: '48%', height: 34 }} />
                <span style={{ width: '100%' }} />
                <span style={{ width: '96%' }} />
                <span style={{ width: '90%' }} />
                <span style={{ width: '70%' }} />
                <span style={{ width: '100%', marginTop: 28 }} />
                <span style={{ width: '92%' }} />
              </div>
            ) : (
              <>
                {error && (
                  <div className="ffp-notice" role="alert">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.2" />
                      <path d="M8 5v3.5M8 11v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
                <div className="ffp-prose" dangerouslySetInnerHTML={{ __html: html }} />
              </>
            )}
          </article>
        </div>
      </div>
    </main>
  );
};

export default Pages;